/**
 * A PDF's bytes, read for pdf.js. The URL is untrusted frame content, so
 * the document is never framed in the privileged webview: its bytes are
 * fetched directly over https only from a public host (or from our own
 * uploads), without credentials or referrer, capped in size, and must
 * start like a PDF. There is no server proxy.
 *
 * Accepted risks, both limited to one credential-less GET whose response a
 * private host keeps unreadable without its own CORS headers: a redirect to
 * a private host (see `readPdfResponse`), and a public DNS name that
 * resolves to a private address (e.g. `127.0.0.1.nip.io`), which the
 * hostname check cannot see. Blocking either needs resolver-side checks,
 * which only a backend proxy could make, and that proxy would bring
 * server-side SSRF.
 */
import { isPrivateHost } from "@/lib/private-host";
import { isOwnUploadUrl } from "@/lib/upload-url";

/** Larger documents are not read. */
export const MAX_PDF_BYTES = 40 * 1024 * 1024;
/** A host that has not answered by then counts as failed. */
const PDF_FETCH_TIMEOUT_MS = 30_000;
/** `%PDF-` must appear this early (the spec allows leading bytes). */
const MAGIC_WINDOW = 1024;
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d];

/**
 * Why a PDF could not be read: `refused` (not https, a private host, or a
 * site that refuses cross-origin reads), `not-pdf`, `too-large`, or
 * `failed` (the site answered with an error, timed out, or pdf.js could
 * not read the document); for a file linked on this computer also
 * `missing` (moved or deleted), `unlinked` (this computer has no link for
 * the frame) or `denied` (macOS keeps its folder from Denker).
 */
export type PdfReadError =
  | "refused"
  | "not-pdf"
  | "too-large"
  | "failed"
  | "missing"
  | "unlinked"
  | "denied";

export type PdfBytes = { bytes: Uint8Array } | { error: PdfReadError };

function startsLikePdf(bytes: Uint8Array): boolean {
  const end = Math.min(bytes.length, MAGIC_WINDOW) - PDF_MAGIC.length;
  for (let at = 0; at <= end; at += 1) {
    if (PDF_MAGIC.every((byte, offset) => bytes[at + offset] === byte)) {
      return true;
    }
  }
  return false;
}

/** True when `url` is https on a public host. */
function isPublicHttps(url: string): boolean {
  try {
    const target = new URL(url);
    return target.protocol === "https:" && !isPrivateHost(target.hostname);
  } catch {
    return false;
  }
}

/**
 * The PDF bytes at `url`, or why they could not be read: not https (before
 * and after redirects) or a host on this machine or network, a refused,
 * failed or slow request, over {@link MAX_PDF_BYTES}, or not a PDF. One of
 * our own uploads is read from the API host on any scheme, under the same
 * rules, and must still be one after redirects.
 */
export async function fetchPdfBytes(
  url: string,
  signal?: AbortSignal,
): Promise<PdfBytes> {
  const allowed = isOwnUploadUrl(url) ? isOwnUploadUrl : isPublicHttps;
  if (!allowed(url)) return { error: "refused" };
  // One controller for the caller's signal and the timeout
  // (`AbortSignal.any` is newer than Safari 16).
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timer = window.setTimeout(abort, PDF_FETCH_TIMEOUT_MS);
  signal?.addEventListener("abort", abort, { once: true });
  try {
    return await readPdfResponse(new URL(url).href, allowed, controller.signal);
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

async function readPdfResponse(
  url: string,
  allowed: (url: string) => boolean,
  signal: AbortSignal,
): Promise<PdfBytes> {
  let response: Response;
  try {
    response = await fetch(url, {
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal,
    });
  } catch {
    // A browser hides why a request failed: offline, timed out, or (most
    // often for a reachable site) refused by its cross-origin policy.
    return {
      error: signal.aborted || !navigator.onLine ? "failed" : "refused",
    };
  }
  // A browser fetch cannot read a redirect's Location (an opaque redirect),
  // so redirects are followed and the gate is applied again to the final
  // URL: a body from a non-https or private host (or, for one of our
  // uploads, from anywhere but the upload) is never read. Residual
  // risk, accepted: the credential-less GET still reaches the redirect
  // target, though a private host's response stays unreadable without its
  // own CORS headers. Full protection needs resolver-side checks, which only
  // a backend proxy could make, and that proxy would bring server-side SSRF.
  if (!allowed(response.url)) {
    void response.body?.cancel().catch(() => undefined);
    return { error: "refused" };
  }
  if (!response.ok) {
    void response.body?.cancel().catch(() => undefined);
    return { error: "failed" };
  }
  const declared = Number(response.headers.get("content-length"));
  if (declared > MAX_PDF_BYTES) {
    void response.body?.cancel().catch(() => undefined);
    return { error: "too-large" };
  }
  if (!response.body) return { error: "failed" };
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.length;
      if (size > MAX_PDF_BYTES) {
        await reader.cancel();
        return { error: "too-large" };
      }
      chunks.push(value);
    }
  } catch {
    return { error: "failed" };
  }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  return startsLikePdf(bytes) ? { bytes } : { error: "not-pdf" };
}
